import classNames from "classnames";
import first from "lodash/first";
import { FC, memo, useMemo } from "react";
import ConditionalWrapper from "~@/components/base/ConditionalWrapper";
import { TariffOutput } from "~@/graphql/codegen/generated";
import { Check14x14Icon } from "~@/icons";
import { RootState, useAppDispatch, useAppSelector } from "~@/store";
import { selectTariff } from "~@/store/commons/pricing.slice";

type TariffCardProps = TariffOutput & {
  isOnly?: boolean;
  isCurrent?: boolean;
  defaultChecked?: boolean;
};

const TariffCard: FC<TariffCardProps> = ({ ...props }) => {
  const {
    id,
    name,
    products,
    isOnly = false,
    isCurrent = false,
    defaultChecked = false,
  } = props;

  const dispatch = useAppDispatch();
  const selectedTariff = useAppSelector(
    (state: RootState) => state.pricing.selectedTariff
  );

  const subscriptionProduct = useMemo(() => {
    return first(products);
  }, [products]);

  const subscriptionPrice = useMemo(() => {
    return subscriptionProduct?.price ?? 0;
  }, [subscriptionProduct]);

  return (
    <ConditionalWrapper
      condition={isCurrent || isOnly}
      truthfulWrapper={(children) => (
        <div
          className={classNames("tariff-card", {
            "tariff-card--is-current": isCurrent,
            "tariff-card--is-focused": isOnly,
          })}
        >
          {children}
        </div>
      )}
      untruthfulWrapper={(children) => (
        <label className="tariff-card" htmlFor={id}>
          {children}
        </label>
      )}
    >
      <div className="tariff-card__left">
        <div className="tariff-card__item tariff-card__item--is-flex-grow-0 tariff-card__item--is-basis-auto">
          {!isCurrent && !isOnly ? (
            <div className="checkbox">
              <input
                id={id}
                type="checkbox"
                checked={
                  selectedTariff ? selectedTariff.id === id : defaultChecked
                }
                onChange={() => {
                  dispatch(selectTariff({ ...props }));
                }}
              />
              <div className="checkbox__indicator" />
            </div>
          ) : (
            <div style={{ width: 14, height: 14 }}></div>
          )}
        </div>
        <div className="tariff-card__item">
          <div className="block block--has-mb-0 block--has-px-15 block--is-overflow-hidden">
            <div className="block block--is-flex block--is-vcentered block--has-mb-6">
              <h1 className="tariff-card__name">{name}</h1>
              {isCurrent && (
                <div className="tariff-card__status">
                  <div className="icon-text icon-text--has-gap-4">
                    <span className="icon icon--is-14x14">
                      <Check14x14Icon />
                    </span>
                    <span>Current plan</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="tariff-card__right">
        <div className="tariff-card__item tariff-card__item--is-flex-grow-0 tariff-card__item--is-basis-auto">
          <div className="tariff-card__price-block">
            <h2 className="tariff-card__price">
              {"$" + subscriptionPrice / 100}
            </h2>
            <p className="tariff-card__price-description">
              {subscriptionProduct?.description ?? "monthly subscription"}
            </p>
          </div>
        </div>
      </div>
    </ConditionalWrapper>
  );
};

export default memo(TariffCard);
